import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** Roman numeral + poetic heading + whispered body, staggered like a slow exhale. */
export function ChapterText({
  numeral,
  title,
  children,
}: {
  numeral: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <header className="space-y-6">
      <p
        className="animate-zen-fade-up font-sans text-[11px] uppercase tracking-[0.55em] text-amber-100/50"
        style={{ animationDelay: '150ms' }}
      >
        {numeral}
      </p>
      <h2
        className="animate-zen-fade-up zen-text-glow mx-auto max-w-xl font-serif text-4xl leading-tight text-slate-100 sm:text-5xl"
        style={{ animationDelay: '350ms' }}
      >
        {title}
      </h2>
      {children && (
        <div
          className="animate-zen-fade-up mx-auto max-w-md font-serif text-lg italic leading-relaxed text-slate-300/85 sm:text-xl"
          style={{ animationDelay: '600ms' }}
        >
          {children}
        </div>
      )}
    </header>
  );
}

/** A small, quiet prompt telling the traveler what to touch. */
export function Whisper({
  children,
  delay = 900,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'animate-zen-fade-up font-sans text-[11px] uppercase tracking-[0.4em] text-white/40',
        className,
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </p>
  );
}

/** A calm caption that appears after an interaction resolves. */
export function Revelation({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'animate-zen-fade-up mx-auto max-w-md font-serif text-lg italic leading-relaxed text-amber-100/80 sm:text-xl',
        className,
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </p>
  );
}

/** The single ghost-button used to move through the journey. */
export function ContinueButton({
  onClick,
  label = 'continue',
  delay = 0,
  className,
}: {
  onClick: () => void;
  label?: string;
  delay?: number;
  className?: string;
}) {
  return (
    <div
      className={cn('animate-zen-fade-up', className)}
      style={{ animationDelay: `${delay}ms` }}
    >
      <button
        type="button"
        onClick={onClick}
        className="rounded-full border border-white/20 px-10 py-3.5 font-sans text-[11px] uppercase tracking-[0.4em] text-white/70 transition-all duration-700 hover:border-amber-100/50 hover:text-amber-100 hover:shadow-[0_0_36px_rgba(253,224,171,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-100/60 active:scale-[0.98]"
      >
        {label}
      </button>
    </div>
  );
}

/** The traveler's light — a warm breathing orb. */
export function PlayerOrb({
  size = 96,
  className,
  style,
}: {
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn('relative', className)}
      style={{ width: size, height: size, ...style }}
      aria-hidden="true"
    >
      <div
        className="animate-zen-breathe absolute -inset-1/2 rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(253, 224, 171, 0.28) 0%, rgba(253, 224, 171, 0.08) 45%, transparent 70%)',
        }}
      />
      <div
        className="animate-zen-breathe absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(circle at 35% 30%, #fff8e7 0%, #f6d493 48%, #c9963f 100%)',
          boxShadow:
            '0 0 40px rgba(246, 212, 147, 0.55), 0 0 90px rgba(246, 212, 147, 0.25)',
        }}
      />
    </div>
  );
}

/** Small dots marking the path through the seven moments. */
export function ProgressDots({
  total,
  current,
}: {
  total: number;
  current: number;
}) {
  return (
    <div className="fixed bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className={cn(
            'rounded-full transition-all duration-1000',
            i === current
              ? 'h-1.5 w-6 bg-amber-200 shadow-[0_0_12px_rgba(253,224,171,0.7)]'
              : i < current
                ? 'h-1.5 w-1.5 bg-white/40'
                : 'h-1.5 w-1.5 bg-white/15',
          )}
        />
      ))}
    </div>
  );
}
